import { useEffect, useMemo, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useAction, useMutation, useQuery } from "convex/react";
import { Check, Sparkles } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { InviteSplitButton } from "@/components/invite-split-button";
import { ResponsiveDialog } from "@/components/responsive-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api } from "@/lib/convex-api";
import { formatTrialRemaining } from "@/lib/billing-utils";
import { decideBillingEmailDraftAction } from "@/lib/billingEmailDraft";
import { formatUserFacingError } from "@/lib/errorMessages";
import { toast } from "sonner";
import type { Id } from "../../../../backend/convex/_generated/dataModel";

// ---------------------------------------------------------------------------
// Plan Picker Dialog
// ---------------------------------------------------------------------------

const MONTHLY_PRICE = 14.99;
const YEARLY_PRICE = 99.0;
const YEARLY_MONTHLY_EQUIVALENT = YEARLY_PRICE / 12;
const YEARLY_SAVINGS_PERCENT = Math.round(
  ((MONTHLY_PRICE * 12 - YEARLY_PRICE) / (MONTHLY_PRICE * 12)) * 100,
);

function PlanPickerDialog({
  open,
  onOpenChange,
  onSelectPlan,
  isBusy,
  seatCount,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectPlan: (plan: "monthly" | "yearly") => void;
  isBusy: boolean;
  seatCount: number;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Choose your plan</DialogTitle>
          <DialogDescription>
            Per seat pricing ({seatCount} seat{seatCount !== 1 ? "s" : ""}).
            Cancel anytime.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2">
          {/* Monthly card */}
          <div className="group relative order-2 flex flex-col items-start rounded-lg border border-border bg-card p-4 text-left transition-colors hover:border-foreground/30 sm:order-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Monthly
            </p>
            <p className="mt-2 flex items-baseline gap-1.5">
              <span className="text-2xl font-bold tracking-tight">
                ${MONTHLY_PRICE.toFixed(2)}
              </span>
              <span className="text-xs text-muted-foreground">
                per seat / month
              </span>
            </p>

            <ul className="mt-4 space-y-1.5 text-xs text-muted-foreground">
              <li className="flex items-center gap-1.5">
                <Check className="size-3 shrink-0 text-foreground" />
                Full feature access
              </li>
              <li className="flex items-center gap-1.5">
                <Check className="size-3 shrink-0 text-foreground" />
                Cancel anytime
              </li>
            </ul>

            <div className="mt-auto w-full pt-4">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="h-8 w-full text-xs"
                disabled={isBusy}
                onClick={() => onSelectPlan("monthly")}
              >
                {isBusy ? "Opening..." : "Choose Monthly"}
              </Button>
            </div>
          </div>

          {/* Yearly card — highlighted */}
          <div className="group relative order-1 flex flex-col items-start rounded-lg border-2 border-primary bg-card p-4 text-left shadow-sm transition-colors hover:shadow-md sm:order-2">
            {/* Recommended badge */}
            <span className="absolute -top-2.5 right-3 inline-flex items-center gap-1 rounded-full bg-primary px-2 py-0.5 text-[10px] font-semibold text-primary-foreground">
              <Sparkles className="size-3" />
              Save {YEARLY_SAVINGS_PERCENT}%
            </span>

            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              Yearly
            </p>
            <p className="mt-2 flex items-baseline gap-1.5">
              <span className="text-2xl font-bold tracking-tight">
                ${YEARLY_MONTHLY_EQUIVALENT.toFixed(2)}
              </span>
              <span className="text-xs text-muted-foreground">
                per seat / month
              </span>
            </p>
            <p className="text-xs text-muted-foreground/70">
              ($99/seat billed annually)
            </p>

            <ul className="mt-4 space-y-1.5 text-xs text-muted-foreground">
              <li className="flex items-center gap-1.5">
                <Check className="size-3 shrink-0 text-primary" />
                Full feature access
              </li>
              <li className="flex items-center gap-1.5">
                <Check className="size-3 shrink-0 text-primary" />
                Cancel anytime
              </li>
              <li className="flex items-center gap-1.5">
                <Check className="size-3 shrink-0 text-primary" />
                Best value
              </li>
            </ul>

            <div className="mt-auto w-full pt-4">
              <Button
                type="button"
                size="sm"
                className="h-8 w-full text-xs"
                disabled={isBusy}
                onClick={() => onSelectPlan("yearly")}
              >
                {isBusy ? "Opening..." : "Choose Yearly"}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Organization Settings
// ---------------------------------------------------------------------------

interface OrganizationSettingsProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizationId: Id<"workspaces"> | null;
  onDeleted?: () => void;
}

const organizationInviteSchema = z.object({
  email: z.string().trim().email("Enter a valid email address."),
  role: z.union([z.literal("owner"), z.literal("member"), z.literal("guest")]),
});

type OrganizationInviteFormValues = z.infer<typeof organizationInviteSchema>;

export const OrganizationSettings = ({
  open,
  onOpenChange,
  organizationId,
  onDeleted,
}: OrganizationSettingsProps) => {
  const user = useQuery(api.users.me);
  const organizations = useQuery(api.organizations.listForUser);
  const currentOrganizationRole =
    organizations?.find((item) => item.id === organizationId)?.role ?? null;
  const canManageExternalAgents = currentOrganizationRole === "owner";
  const externalAgentWorkspaceId =
    organizationId && canManageExternalAgents ? organizationId : null;
  const members = useQuery(
    api.organizations.listMembers,
    organizationId ? { organizationId } : "skip",
  );
  const updateName = useMutation(api.organizations.updateName);
  const updateBillingEmail = useMutation(api.organizations.updateBillingEmail);
  const addMember = useMutation(api.organizations.addMember);
  const addMemberAndSendEmail = useAction(
    api.organizations.addMemberAndSendEmail,
  );
  const pendingInvites = useQuery(
    api.organizations.listPendingInvites,
    organizationId && currentOrganizationRole === "owner"
      ? { organizationId }
      : "skip",
  );
  const updateMemberRole = useMutation(api.organizations.updateMemberRole);
  const removeMember = useMutation(api.organizations.removeMember);
  const revokeInvite = useMutation(api.organizations.revokeInvite);
  const refreshInviteLink = useMutation(api.organizations.refreshInviteLink);
  const deleteOrganization = useAction(api.organizations.deleteOrganization);
  const billingState = useQuery(
    api.billing.getOrganizationBillingState,
    organizationId ? { organizationId } : "skip",
  );
  const validateBillingEmailForCheckout = useAction(
    api.billing.validateBillingEmailForCheckout,
  );
  const createCheckoutSession = useAction(api.billing.createCheckoutSession);
  const createCustomerPortalSession = useAction(
    api.billing.createCustomerPortalSession,
  );
  const projectsForUser = useQuery(api.projects.listForUser);
  const externalAgents = useQuery(
    api.externalAgents.listExternalAgents,
    externalAgentWorkspaceId
      ? { workspaceId: externalAgentWorkspaceId }
      : "skip",
  );
  const createExternalAgent = useMutation(
    api.externalAgents.createExternalAgent,
  );
  const updateExternalAgent = useMutation(
    api.externalAgents.updateExternalAgent,
  );
  const createExternalAgentKey = useMutation(
    api.externalAgents.createExternalAgentKey,
  );
  const revokeExternalAgentKey = useMutation(
    api.externalAgents.revokeExternalAgentKey,
  );
  const grantProjectAccess = useMutation(api.externalAgents.grantProjectAccess);
  const revokeProjectAccess = useMutation(
    api.externalAgents.revokeProjectAccess,
  );

  const [selectedExternalAgentId, setSelectedExternalAgentId] =
    useState<Id<"externalAgents"> | null>(null);
  const [newAgentName, setNewAgentName] = useState("");
  const [newAgentDescription, setNewAgentDescription] = useState("");
  const [newKeyLabel, setNewKeyLabel] = useState("");
  const [issuedKeyToken, setIssuedKeyToken] = useState<string | null>(null);
  const [grantProjectId, setGrantProjectId] = useState<Id<"projects"> | null>(
    null,
  );
  const [grantRole, setGrantRole] = useState<"viewer" | "editor">("viewer");

  const externalAgentKeys = useQuery(
    api.externalAgents.listExternalAgentKeys,
    selectedExternalAgentId
      ? { externalAgentId: selectedExternalAgentId }
      : "skip",
  );
  const externalAgentGrants = useQuery(
    api.externalAgents.listProjectGrants,
    selectedExternalAgentId
      ? { externalAgentId: selectedExternalAgentId }
      : "skip",
  );
  const externalAgentAudit = useQuery(
    api.externalAgents.listAuditEvents,
    externalAgentWorkspaceId
      ? {
          workspaceId: externalAgentWorkspaceId,
          externalAgentId: selectedExternalAgentId ?? undefined,
          limit: 20,
        }
      : "skip",
  );

  const organization = useMemo(
    () => organizations?.find((item) => item.id === organizationId) ?? null,
    [organizationId, organizations],
  );
  const ownerCount =
    members?.filter((member) => member.role === "owner").length ?? 0;
  const projectsInOrganization = useMemo(
    () =>
      (projectsForUser ?? []).filter(
        (project) => project.workspaceId === organizationId,
      ),
    [organizationId, projectsForUser],
  );
  const selectedExternalAgent = useMemo(
    () =>
      externalAgents?.find((agent) => agent._id === selectedExternalAgentId) ??
      null,
    [externalAgents, selectedExternalAgentId],
  );

  useEffect(() => {
    if (!externalAgents || externalAgents.length === 0) {
      setSelectedExternalAgentId(null);
      return;
    }
    const selectedExists = selectedExternalAgentId
      ? externalAgents.some((agent) => agent._id === selectedExternalAgentId)
      : false;
    if (!selectedExists) {
      setSelectedExternalAgentId(externalAgents[0]!._id);
    }
  }, [externalAgents, selectedExternalAgentId]);

  const [nameDraft, setNameDraft] = useState("");
  // `null` means "unchanged"; empty string means "user cleared the input".
  const [billingEmailDraft, setBillingEmailDraft] = useState<string | null>(
    null,
  );
  const [billingEmailNeedsAttention, setBillingEmailNeedsAttention] =
    useState(false);
  const [billingEmailValidationState, setBillingEmailValidationState] =
    useState<"idle" | "checking" | "ok" | "conflict" | "error">("idle");
  const billingEmailInputRef = useRef<HTMLInputElement | null>(null);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [deleteName, setDeleteName] = useState("");
  const [isBillingBusy, setIsBillingBusy] = useState(false);
  const [planPickerOpen, setPlanPickerOpen] = useState(false);
  const inviteForm = useForm<OrganizationInviteFormValues>({
    resolver: zodResolver(organizationInviteSchema),
    defaultValues: {
      email: "",
      role: "member",
    },
  });
  const organizationBillingEmail = organization?.billingEmail ?? "";
  const effectiveBillingEmail =
    billingEmailDraft === null ? organizationBillingEmail : billingEmailDraft;
  const canManageMembers = currentOrganizationRole === "owner";
  const billingOverrideActive =
    billingState?.status === "active" && !billingState.hasPolarSubscriptionId;

  async function copyInviteLink(inviteUrl: string): Promise<void> {
    const absoluteUrl = new URL(
      inviteUrl,
      globalThis.location.origin,
    ).toString();
    try {
      await globalThis.navigator.clipboard.writeText(absoluteUrl);
      toast.success("Invite link copied.");
    } catch {
      toast.error("Failed to copy invite link.");
    }
  }

  function handleBillingEmailConflict(): void {
    setPlanPickerOpen(false);
    setBillingEmailNeedsAttention(true);
    setBillingEmailValidationState("conflict");
    toast.error(
      "Billing email is already used by another organization. Please set a different billing email and try again.",
    );
    // Defer focus until after any dialog state updates.
    setTimeout(() => billingEmailInputRef.current?.focus(), 0);
  }

  function errorMessageIncludes(err: unknown, needleLower: string): boolean {
    const raw =
      err instanceof Error
        ? err.message
        : typeof err === "string"
          ? err
          : err && typeof err === "object" && "message" in err
            ? String((err as { message?: unknown }).message ?? "")
            : "";
    return raw.toLowerCase().includes(needleLower);
  }

  // Proactively validate billing email while viewing settings so we can guide
  // the user before they ever get to the plan picker.
  useEffect(() => {
    if (!open) return;
    if (!organizationId) return;
    if (!billingState?.canManageBilling) return;
    if (billingState.status === "active") return;
    if (isBillingBusy) return;

    const candidate = effectiveBillingEmail.trim();
    if (!candidate || !candidate.includes("@")) return;

    setBillingEmailValidationState("checking");
    const t = window.setTimeout(() => {
      void validateBillingEmailForCheckout({
        organizationId,
        billingEmail: candidate,
      })
        .then(() => {
          setBillingEmailValidationState("ok");
          // Only clear the "needs attention" flag if we're validating the saved value.
          if (billingEmailDraft === null) setBillingEmailNeedsAttention(false);
        })
        .catch((err) => {
          if (errorMessageIncludes(err, "billing_email_conflict:")) {
            setBillingEmailValidationState("conflict");
            setBillingEmailNeedsAttention(true);
            return;
          }
          // Don't spam toasts for background validation; keep it subtle.
          setBillingEmailValidationState("error");
        });
    }, 400);

    return () => window.clearTimeout(t);
  }, [
    open,
    organizationId,
    billingState?.canManageBilling,
    billingState?.status,
    isBillingBusy,
    effectiveBillingEmail,
    billingEmailDraft,
    validateBillingEmailForCheckout,
  ]);

  if (!organizationId || !organization) {
    return null;
  }

  const org = organization;
  const orgId = organizationId;

  async function handleAddMember(
    values: OrganizationInviteFormValues,
    sendEmail: boolean,
  ): Promise<void> {
    const email = values.email.trim();
    setIsAddingMember(true);
    try {
      if (sendEmail) {
        const result = await addMemberAndSendEmail({
          organizationId: orgId,
          email,
          role: values.role,
        });
        await copyInviteLink(result.inviteUrl);
        if (!result.emailSent) {
          toast.info(
            "Invite link created, but no email was sent because email delivery is unavailable.",
          );
        }
      } else {
        const result = await addMember({
          organizationId: orgId,
          email,
          role: values.role,
        });
        await copyInviteLink(result.inviteUrl);
      }
      inviteForm.reset({
        email: "",
        role: values.role,
      });
    } catch (error) {
      toast.error(formatUserFacingError(error, "Failed to add member."));
    } finally {
      setIsAddingMember(false);
    }
  }

  async function maybeSaveBillingEmailDraft(): Promise<string | null> {
    const decision = decideBillingEmailDraftAction({
      savedBillingEmail: org.billingEmail ?? "",
      draftBillingEmail: billingEmailDraft,
    });

    if (decision.kind === "noop") {
      // If the user typed the same value as the saved value, clear the draft so
      // follow-up actions don't think there's unsaved state.
      if (billingEmailDraft !== null) setBillingEmailDraft(null);
      return decision.billingEmail;
    }

    if (
      decision.kind === "invalid_empty" ||
      decision.kind === "invalid_format"
    ) {
      setBillingEmailNeedsAttention(true);
      toast.error("Enter a valid billing email address.");
      setTimeout(() => billingEmailInputRef.current?.focus(), 0);
      return null;
    }

    try {
      await updateBillingEmail({
        organizationId: orgId,
        billingEmail: decision.billingEmail,
      });
      setBillingEmailDraft(null);
      setBillingEmailNeedsAttention(false);
      return decision.billingEmail;
    } catch (error) {
      toast.error(
        formatUserFacingError(error, "Failed to update billing email."),
      );
      setBillingEmailNeedsAttention(true);
      setTimeout(() => billingEmailInputRef.current?.focus(), 0);
      return null;
    }
  }

  return (
    <ResponsiveDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Organization Settings"
    >
      <div className="space-y-4">
        <section className="space-y-2">
          <p className="text-xs text-muted-foreground">Organization name</p>
          <div className="flex items-center gap-2">
            <Input
              value={nameDraft || organization.name}
              onChange={(event) => setNameDraft(event.target.value)}
            />
            <Button
              size="sm"
              onClick={() => {
                const next = nameDraft.trim();
                if (!next || next === organization.name) return;
                void updateName({ organizationId, name: next })
                  .then(() => {
                    setNameDraft("");
                  })
                  .catch((error) =>
                    toast.error(
                      formatUserFacingError(
                        error,
                        "Failed to update organization.",
                      ),
                    ),
                  );
              }}
            >
              Save
            </Button>
          </div>
        </section>

        <section className="space-y-2">
          <p className="text-xs text-muted-foreground">Billing</p>
          <div className="rounded border px-3 py-2 space-y-2">
            <p className="text-xs">
              Status:{" "}
              <span className="font-medium capitalize">
                {billingState?.status ?? "loading"}
              </span>
            </p>
            {billingState?.status === "trial" &&
              typeof billingState.trialEndsAt === "number" &&
              billingState.trialEndsAt > Date.now() && (
                <p className="text-xs text-amber-700 dark:text-amber-300">
                  Your free trial ends{" "}
                  {formatTrialRemaining(billingState.trialEndsAt)}.
                </p>
              )}
            {billingState?.status === "active" && (
              <p className="text-xs text-muted-foreground">
                {billingState.currentSeats ?? 0} seat
                {(billingState.currentSeats ?? 0) !== 1 ? "s" : ""}{" "}
                {billingState.billingCadence
                  ? `(${billingState.billingCadence})`
                  : ""}
              </p>
            )}
            {billingOverrideActive ? (
              <p className="text-xs text-muted-foreground">
                Local dev billing override is enabled, so this organization is
                treated as subscribed.
              </p>
            ) : null}
            {billingState?.canManageBilling ? (
              <div className="space-y-1">
                <p className="text-[11px] text-muted-foreground">
                  Billing email
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    value={billingEmailDraft ?? organization.billingEmail ?? ""}
                    onChange={(event) => {
                      setBillingEmailDraft(event.target.value);
                      if (billingEmailNeedsAttention) {
                        setBillingEmailNeedsAttention(false);
                      }
                    }}
                    placeholder={user?.email ?? "Defaults to your login email"}
                    className={`h-8 text-xs ${billingEmailNeedsAttention ? "border-destructive focus-visible:ring-destructive" : ""}`}
                    disabled={isBillingBusy}
                    ref={billingEmailInputRef}
                  />
                  <Button
                    size="xs"
                    variant="outline"
                    disabled={isBillingBusy}
                    onClick={() => {
                      // If the user hasn't changed the field, there's nothing to save.
                      if (billingEmailDraft === null) return;

                      const next = billingEmailDraft.trim();
                      if (!next) {
                        toast.error("Enter a billing email address.");
                        return;
                      }
                      if (
                        organization.billingEmail &&
                        next.toLowerCase() ===
                          organization.billingEmail.toLowerCase()
                      ) {
                        setBillingEmailDraft(null);
                        return;
                      }
                      setIsBillingBusy(true);
                      void updateBillingEmail({
                        organizationId,
                        billingEmail: next,
                      })
                        .then(() => {
                          setBillingEmailDraft(null);
                          setBillingEmailNeedsAttention(false);
                        })
                        .catch((error) =>
                          toast.error(
                            formatUserFacingError(
                              error,
                              "Failed to update billing email.",
                            ),
                          ),
                        )
                        .finally(() => setIsBillingBusy(false));
                    }}
                  >
                    Save
                  </Button>
                </div>
                {!organization.billingEmail ? (
                  <p className="text-[11px] text-muted-foreground">
                    Defaults to your login email. If Polar reports a conflict,
                    set a unique billing email for this organization.
                  </p>
                ) : null}
                {billingEmailNeedsAttention ? (
                  <p className="text-[11px] text-destructive">
                    Billing email is already in use. Please choose a different
                    billing email and try again.
                  </p>
                ) : null}
                {billingEmailValidationState === "checking" ? (
                  <p className="text-[11px] text-muted-foreground">
                    Checking billing email availability...
                  </p>
                ) : null}
              </div>
            ) : null}
            {billingState?.canManageBilling ? (
              billingOverrideActive ? (
                <p className="text-xs text-muted-foreground">
                  Billing actions are disabled while the local dev override is
                  enabled.
                </p>
              ) : billingState.status === "active" ? (
                <Button
                  size="xs"
                  variant="outline"
                  disabled={isBillingBusy}
                  onClick={() => {
                    setIsBillingBusy(true);
                    void createCustomerPortalSession({
                      organizationId,
                    })
                      .then((result) => {
                        window.open(
                          result.url,
                          "_blank",
                          "noopener,noreferrer",
                        );
                      })
                      .catch((error) =>
                        toast.error(
                          formatUserFacingError(
                            error,
                            "Failed to open billing portal.",
                          ),
                        ),
                      )
                      .finally(() => setIsBillingBusy(false));
                  }}
                >
                  {isBillingBusy ? "Opening..." : "Billing Portal"}
                </Button>
              ) : (
                <Button
                  size="xs"
                  disabled={isBillingBusy}
                  onClick={() => {
                    void (async () => {
                      setIsBillingBusy(true);
                      try {
                        const billingEmail = await maybeSaveBillingEmailDraft();
                        if (billingEmail === null) return;

                        await validateBillingEmailForCheckout({
                          organizationId,
                          billingEmail: billingEmail.trim() || undefined,
                        });
                        setPlanPickerOpen(true);
                      } catch (error) {
                        if (
                          errorMessageIncludes(error, "billing_email_conflict:")
                        ) {
                          handleBillingEmailConflict();
                          return;
                        }
                        const raw =
                          error instanceof Error
                            ? error.message
                            : typeof error === "string"
                              ? error
                              : "";
                        const redacted = raw
                          .replace(
                            /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi,
                            "***@***",
                          )
                          .replace(/[a-f0-9]{32,}/gi, "***");
                        // Redacted log for diagnosing why validation fails.
                        console.error(
                          "[billing] validateBillingEmailForCheckout failed",
                          {
                            message: redacted || "(no message)",
                          },
                        );
                        toast.error(
                          formatUserFacingError(
                            error,
                            "Failed to validate billing email.",
                          ),
                        );
                      } finally {
                        setIsBillingBusy(false);
                      }
                    })();
                  }}
                >
                  Subscribe Now
                </Button>
              )
            ) : (
              <p className="text-xs text-muted-foreground">
                Only organization owners can manage billing.
              </p>
            )}
          </div>
        </section>

        <section className="space-y-2">
          <p className="text-xs text-muted-foreground">Members</p>
          <div className="space-y-1">
            {(members ?? []).map((member) => {
              const isLastOwner = member.role === "owner" && ownerCount <= 1;
              return (
                <div
                  key={member.id}
                  className="flex items-center justify-between rounded border px-2 py-1.5"
                >
                  <div>
                    <p className="text-xs font-medium">{member.name}</p>
                    <p className="text-[11px] text-muted-foreground">
                      {member.email}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select
                      disabled={isLastOwner}
                      value={member.role}
                      onValueChange={(value: "owner" | "member" | "guest") => {
                        if (isLastOwner && value !== "owner") return;
                        void updateMemberRole({
                          organizationId,
                          userId: member.userId,
                          role: value,
                        }).catch((error) =>
                          toast.error(
                            formatUserFacingError(
                              error,
                              "Failed to update role.",
                            ),
                          ),
                        );
                      }}
                    >
                      <SelectTrigger className="h-7 w-24 text-[11px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="owner">Owner</SelectItem>
                        {!isLastOwner && (
                          <SelectItem value="member">Member</SelectItem>
                        )}
                        {!isLastOwner && (
                          <SelectItem value="guest">Guest</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                    {!isLastOwner && (
                      <Button
                        size="xs"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => {
                          void removeMember({
                            organizationId,
                            userId: member.userId,
                          }).catch((error) =>
                            toast.error(
                              formatUserFacingError(
                                error,
                                "Failed to remove member.",
                              ),
                            ),
                          );
                        }}
                      >
                        Remove
                      </Button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {canManageMembers ? (
          <section className="space-y-2">
            <p className="text-xs text-muted-foreground">Invite people</p>
            <Form {...inviteForm}>
              <form
                onSubmit={(event) => event.preventDefault()}
                className="space-y-2"
              >
                <div className="flex items-start gap-2">
                  <FormField
                    control={inviteForm.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem className="flex-1 gap-1">
                        <FormControl>
                          <Input
                            type="email"
                            placeholder="user@company.com"
                            className="h-8 text-xs"
                            {...field}
                          />
                        </FormControl>
                        <FormMessage className="text-[11px]" />
                      </FormItem>
                    )}
                  />
                  <FormField
                    control={inviteForm.control}
                    name="role"
                    render={({ field }) => (
                      <FormItem className="w-24 gap-1">
                        <Select
                          value={field.value}
                          onValueChange={(value) =>
                            field.onChange(
                              value as OrganizationInviteFormValues["role"],
                            )
                          }
                        >
                          <FormControl>
                            <SelectTrigger size="sm" className="w-24 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem value="owner">Owner</SelectItem>
                            <SelectItem value="member">Member</SelectItem>
                            <SelectItem value="guest">Guest</SelectItem>
                          </SelectContent>
                        </Select>
                      </FormItem>
                    )}
                  />
                  <InviteSplitButton
                    primaryLabel={
                      isAddingMember ? "Working..." : "Send invite email"
                    }
                    secondaryLabel="Create invite link"
                    size="sm"
                    className="self-start"
                    disabled={isAddingMember}
                    onPrimaryAction={() => {
                      void inviteForm.handleSubmit((values) =>
                        handleAddMember(values, true),
                      )();
                    }}
                    onSecondaryAction={() => {
                      void inviteForm.handleSubmit((values) =>
                        handleAddMember(values, false),
                      )();
                    }}
                  />
                </div>
              </form>
            </Form>
          </section>
        ) : null}

        {canManageMembers ? (
          <section className="space-y-2">
            <p className="text-xs text-muted-foreground">Pending invites</p>
            {(pendingInvites ?? []).length === 0 ? (
              <p className="text-[11px] text-muted-foreground">
                No pending invites.
              </p>
            ) : (
              <div className="space-y-1">
                {(pendingInvites ?? []).map((invite) => (
                  <div
                    key={invite.id}
                    className="flex items-center justify-between rounded border px-2 py-1.5"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium">
                        {invite.email}
                      </p>
                      <p className="truncate text-[11px] text-muted-foreground capitalize">
                        {invite.role} •{" "}
                        {invite.isExpired ? "Expired" : "Pending"}
                      </p>
                    </div>
                    <div className="ml-2 flex items-center gap-1">
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          void copyInviteLink(invite.inviteUrl);
                        }}
                      >
                        Copy link
                      </Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        onClick={() => {
                          void refreshInviteLink({ inviteId: invite.id })
                            .then((result) => {
                              void copyInviteLink(result.inviteUrl);
                              toast.success("Invite link refreshed.");
                            })
                            .catch((error) => {
                              toast.error(
                                formatUserFacingError(
                                  error,
                                  "Failed to refresh invite link.",
                                ),
                              );
                            });
                        }}
                      >
                        Refresh link
                      </Button>
                      <Button
                        size="xs"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => {
                          void revokeInvite({ inviteId: invite.id }).catch(
                            (error) => {
                              toast.error(
                                formatUserFacingError(
                                  error,
                                  "Failed to revoke invite.",
                                ),
                              );
                            },
                          );
                        }}
                      >
                        Revoke
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        ) : null}

        <section className="space-y-3">
          <p className="text-xs text-muted-foreground">External Agents</p>
          {canManageExternalAgents ? (
            <div className="rounded border p-3 space-y-3">
              <div className="space-y-2">
                <Input
                  value={newAgentName}
                  onChange={(event) => setNewAgentName(event.target.value)}
                  placeholder="Agent name (e.g. CI Assistant)"
                  className="h-8 text-xs"
                />
                <Textarea
                  value={newAgentDescription}
                  onChange={(event) =>
                    setNewAgentDescription(event.target.value)
                  }
                  placeholder="Optional description"
                  className="min-h-16 text-xs"
                />
                <p className="text-[11px] text-muted-foreground">
                  External agents use API keys. OAuth access maps to MDSync
                  users.
                </p>
                <Button
                  size="xs"
                  onClick={() => {
                    const name = newAgentName.trim();
                    if (!name) {
                      toast.error("Enter an agent name.");
                      return;
                    }
                    void createExternalAgent({
                      workspaceId: organizationId,
                      name,
                      description: newAgentDescription.trim() || undefined,
                    })
                      .then(() => {
                        setNewAgentName("");
                        setNewAgentDescription("");
                      })
                      .catch((error) =>
                        toast.error(
                          formatUserFacingError(
                            error,
                            "Failed to create external agent.",
                          ),
                        ),
                      );
                  }}
                >
                  Create Agent
                </Button>
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div className="space-y-2">
                  {(externalAgents ?? []).map((agent) => (
                    <button
                      key={agent._id}
                      type="button"
                      onClick={() => {
                        setIssuedKeyToken(null);
                        setSelectedExternalAgentId(agent._id);
                      }}
                      className={`w-full rounded border px-2 py-2 text-left ${
                        selectedExternalAgentId === agent._id
                          ? "border-primary"
                          : "border-border"
                      }`}
                    >
                      <p className="text-xs font-medium">{agent.name}</p>
                      <p className="text-[11px] text-muted-foreground">
                        {agent.status} · {agent.activeKeyCount} active key
                        {agent.activeKeyCount !== 1 ? "s" : ""}
                      </p>
                      {agent.description ? (
                        <p className="text-[11px] text-muted-foreground mt-1">
                          {agent.description}
                        </p>
                      ) : null}
                    </button>
                  ))}
                  {(externalAgents ?? []).length === 0 ? (
                    <p className="text-[11px] text-muted-foreground">
                      No external agents yet.
                    </p>
                  ) : null}
                </div>

                <div className="space-y-3">
                  {selectedExternalAgent ? (
                    <>
                      <div className="flex items-center gap-2">
                        <Button
                          size="xs"
                          variant="outline"
                          onClick={() => {
                            const nextStatus =
                              selectedExternalAgent.status === "active"
                                ? "disabled"
                                : "active";
                            void updateExternalAgent({
                              externalAgentId: selectedExternalAgent._id,
                              status: nextStatus,
                            }).catch((error) =>
                              toast.error(
                                formatUserFacingError(
                                  error,
                                  "Failed to update agent status.",
                                ),
                              ),
                            );
                          }}
                        >
                          {selectedExternalAgent.status === "active"
                            ? "Disable"
                            : "Enable"}
                        </Button>
                        <Input
                          value={newKeyLabel}
                          onChange={(event) =>
                            setNewKeyLabel(event.target.value)
                          }
                          placeholder="Key label (optional)"
                          className="h-8 text-xs"
                        />
                        <Button
                          size="xs"
                          onClick={() => {
                            void createExternalAgentKey({
                              externalAgentId: selectedExternalAgent._id,
                              label: newKeyLabel.trim() || undefined,
                            })
                              .then((result) => {
                                setIssuedKeyToken(result.token);
                                setNewKeyLabel("");
                                toast.success("Key issued. Copy it now.");
                              })
                              .catch((error) =>
                                toast.error(
                                  formatUserFacingError(
                                    error,
                                    "Failed to issue key.",
                                  ),
                                ),
                              );
                          }}
                        >
                          Generate Key
                        </Button>
                      </div>
                      {issuedKeyToken ? (
                        <div className="rounded border border-amber-400/60 bg-amber-50 px-2 py-1 dark:bg-amber-950/30">
                          <p className="text-[11px] font-medium">
                            Copy this key now (shown once):
                          </p>
                          <p className="text-[11px] font-mono break-all">
                            {issuedKeyToken}
                          </p>
                        </div>
                      ) : null}

                      <div className="space-y-1">
                        <p className="text-[11px] font-medium">Keys</p>
                        {(externalAgentKeys ?? []).map((key) => (
                          <div
                            key={key._id}
                            className="flex items-center justify-between rounded border px-2 py-1"
                          >
                            <div>
                              <p className="text-[11px] font-mono">
                                {key.keyPrefix}
                              </p>
                              <p className="text-[10px] text-muted-foreground">
                                {key.revokedAt
                                  ? "revoked"
                                  : key.lastUsedAt
                                    ? `last used ${new Date(key.lastUsedAt).toLocaleString()}`
                                    : "never used"}
                              </p>
                            </div>
                            {!key.revokedAt ? (
                              <Button
                                size="xs"
                                variant="ghost"
                                className="text-destructive"
                                onClick={() => {
                                  void revokeExternalAgentKey({
                                    keyId: key._id,
                                  }).catch((error) =>
                                    toast.error(
                                      formatUserFacingError(
                                        error,
                                        "Failed to revoke key.",
                                      ),
                                    ),
                                  );
                                }}
                              >
                                Revoke
                              </Button>
                            ) : null}
                          </div>
                        ))}
                      </div>

                      <div className="space-y-1">
                        <p className="text-[11px] font-medium">
                          Project Grants
                        </p>
                        <div className="flex items-center gap-2">
                          <Select
                            value={grantProjectId ?? ""}
                            onValueChange={(value) =>
                              setGrantProjectId(value as Id<"projects">)
                            }
                          >
                            <SelectTrigger className="h-8 text-xs">
                              <SelectValue placeholder="Select project" />
                            </SelectTrigger>
                            <SelectContent>
                              {projectsInOrganization.map((project) => (
                                <SelectItem key={project.id} value={project.id}>
                                  {project.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <Select
                            value={grantRole}
                            onValueChange={(value: "viewer" | "editor") =>
                              setGrantRole(value)
                            }
                          >
                            <SelectTrigger className="h-8 w-24 text-xs">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="viewer">Viewer</SelectItem>
                              <SelectItem value="editor">Editor</SelectItem>
                            </SelectContent>
                          </Select>
                          <Button
                            size="xs"
                            onClick={() => {
                              if (!grantProjectId) return;
                              void grantProjectAccess({
                                externalAgentId: selectedExternalAgent._id,
                                projectId: grantProjectId,
                                role: grantRole,
                              }).catch((error) =>
                                toast.error(
                                  formatUserFacingError(
                                    error,
                                    "Failed to grant project access.",
                                  ),
                                ),
                              );
                            }}
                          >
                            Save
                          </Button>
                        </div>
                        {(externalAgentGrants ?? []).map((grant) => (
                          <div
                            key={grant._id}
                            className="flex items-center justify-between rounded border px-2 py-1"
                          >
                            <p className="text-[11px]">
                              {grant.projectName ?? grant.projectId} ·{" "}
                              {grant.role}
                            </p>
                            <Button
                              size="xs"
                              variant="ghost"
                              className="text-destructive"
                              onClick={() => {
                                void revokeProjectAccess({
                                  externalAgentId: selectedExternalAgent._id,
                                  projectId: grant.projectId,
                                }).catch((error) =>
                                  toast.error(
                                    formatUserFacingError(
                                      error,
                                      "Failed to revoke grant.",
                                    ),
                                  ),
                                );
                              }}
                            >
                              Remove
                            </Button>
                          </div>
                        ))}
                      </div>

                      <div className="space-y-1">
                        <p className="text-[11px] font-medium">
                          Recent Audit Events
                        </p>
                        {(externalAgentAudit?.items ?? [])
                          .slice(0, 8)
                          .map((event) => (
                            <div
                              key={event._id}
                              className="rounded border px-2 py-1 text-[10px] text-muted-foreground"
                            >
                              <span className="font-medium text-foreground">
                                {event.toolName}
                              </span>{" "}
                              ·{" "}
                              {event.success
                                ? "ok"
                                : `error (${event.errorCode ?? "unknown"})`}{" "}
                              · {event.durationMs}ms
                            </div>
                          ))}
                      </div>
                    </>
                  ) : (
                    <p className="text-[11px] text-muted-foreground">
                      Select an agent to manage keys and grants.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="rounded border p-3">
              <p className="text-[11px] text-muted-foreground">
                Only organization owners can manage external agents.
              </p>
            </div>
          )}
        </section>

        <section className="rounded border border-destructive/30 p-3 space-y-2">
          <p className="text-xs font-medium text-destructive">
            Delete organization
          </p>
          <p className="text-[11px] text-muted-foreground">
            Type <span className="font-mono">{organization.name}</span> to
            confirm.
          </p>
          <div className="flex items-center gap-2">
            <Input
              value={deleteName}
              onChange={(event) => setDeleteName(event.target.value)}
              className="h-8 text-xs"
            />
            <Button
              variant="destructive"
              size="xs"
              disabled={deleteName !== organization.name}
              onClick={() => {
                void deleteOrganization({ organizationId })
                  .then(() => {
                    onDeleted?.();
                    onOpenChange(false);
                  })
                  .catch((error) =>
                    toast.error(
                      formatUserFacingError(
                        error,
                        "Failed to delete organization.",
                      ),
                    ),
                  );
              }}
            >
              Delete
            </Button>
          </div>
        </section>
      </div>

      <PlanPickerDialog
        open={planPickerOpen}
        onOpenChange={setPlanPickerOpen}
        isBusy={isBillingBusy}
        seatCount={billingState?.seatCount ?? 1}
        onSelectPlan={(plan) => {
          void (async () => {
            setIsBillingBusy(true);
            try {
              const billingEmail = await maybeSaveBillingEmailDraft();
              if (billingEmail === null) return;

              const result = await createCheckoutSession({
                organizationId,
                plan,
              });
              setPlanPickerOpen(false);
              window.open(result.url, "_blank", "noopener,noreferrer");
            } catch (error) {
              if (errorMessageIncludes(error, "billing_email_conflict:")) {
                handleBillingEmailConflict();
                return;
              }
              toast.error(
                formatUserFacingError(error, "Failed to open checkout."),
              );
            } finally {
              setIsBillingBusy(false);
            }
          })();
        }}
      />
    </ResponsiveDialog>
  );
};
